import React from "react"
import hash from "object-hash"
import Analytics from "analytics"
import { PageProps } from "gatsby"

import { facebookPixelPlugin } from "./facebookPixelPlugin"
import { klaviyoEventPlugin } from "./klaviyoEventPlugin"
import { ViewProductPayload, AddToCartPayload } from "./types"

const debugMode = "development" === process.env.NODE_ENV

const debug = (message: string, context: any) => {
  if (!debugMode) return
  console.info(`ANALYTICS: ${message}`, context)
}

interface Settings {
  facebook: {
    trackingId: string
  }
}

interface ProviderProps {
  pageProps: PageProps
  settings: Settings
}

type AnalyticsContext = {
  addToCart: (payload: AddToCartPayload) => void
  viewProduct: (payload: ViewProductPayload) => void
}

export const AnalyticsContext = React.createContext<AnalyticsContext>(null)

export const AnalyticsProvider: React.FC<ProviderProps> = ({ children, pageProps, settings }) => {
  const analytics = React.useMemo(() => {
    if (typeof window == `undefined`) return null

    const facebookPixelId = "development" === process.env.NODE_ENV ? "TEST" : settings.facebook.trackingId

    return Analytics({
      plugins: [facebookPixelId && facebookPixelPlugin({ pixelId: facebookPixelId }), klaviyoEventPlugin()].filter(Boolean),
    })
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // keep track of identical payloads, until the page changes
  const [addToCartHashes, setAddToCartHashes] = React.useState<string[]>([])
  const [viewProductHashes, setViewProductHashes] = React.useState<string[]>([])

  // track pageview when pageProps change
  React.useEffect(() => {
    if (!analytics) return
    analytics.page()
    setAddToCartHashes([])
    setViewProductHashes([])
    debug("pageview tracked", pageProps.location)
  }, [analytics, pageProps])

  const viewProduct = React.useCallback(
    (payload: ViewProductPayload) => {
      if (!analytics) return

      // prevent identical payloads being sent multiple times
      const viewProductHash = hash(payload)
      if (viewProductHashes.includes(viewProductHash)) return
      setViewProductHashes(viewProductHashes => [...viewProductHashes, viewProductHash])

      analytics.track("viewProduct", payload)
      debug("View Product tracked", payload)
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [viewProductHashes]
  )
  const addToCart = React.useCallback(
    (payload: AddToCartPayload) => {
      if (!analytics) return

      // prevent identical payloads being sent multiple times
      const addToCartHash = hash(payload)
      if (addToCartHashes.includes(addToCartHash)) return
      setAddToCartHashes(addToCartHashes => [...addToCartHashes, addToCartHash])

      analytics.track("addToCart", payload)

      debug("add to cart tracked", payload)
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [addToCartHashes]
  )
  //eslint-disable-next-line react-hooks/exhaustive-deps
  const contextValue = React.useMemo(() => ({ viewProduct, addToCart }), [viewProduct])

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>
}

export const useAnalytics = () => React.useContext(AnalyticsContext)
